// Bileli · modules/account/model — the profile PHOTO, Telegram-first (user decision: no manual file
// picker). The fork IS a Telegram client, so the user's real Telegram profile photo is available as
// bytes: getChatAvatarHash(user,'big') yields a media hash ONLY when the user has a photo (that's the
// has-photo check), mediaLoader downloads it as a blob, and the existing media pipeline uploads it to
// the caller's personal org before PUT /me/avatar binds it. No Telegram photo → the profile hands off
// to Telegram's own Edit-Profile settings instead.

import { getGlobal } from '../../../../global';

import type { BileliUser } from '../../../../util/bileli/types';
import { ApiMediaFormat } from '../../../../api/types';

import { getChatAvatarHash } from '../../../../global/helpers';
import { selectUser } from '../../../../global/selectors';
import * as mediaLoader from '../../../../util/mediaLoader';
import { bileliApiJson, parseApi, schemas } from '../../../shared';
import { uploadMedia } from '../../media';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** PUT /me/avatar — bind an uploaded IMAGE mediaId (null clears); returns the user with resolved avatarUrl. */
export async function setMyAvatar(mediaId: string | null): Promise<BileliUser> {
  return bileliApiJson<BileliUser>('/me/avatar', {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ mediaId }),
  });
}

/** Does the signed-in TELEGRAM account have a profile photo? (undefined hash = no photo). */
export function hasTelegramPhoto(): boolean {
  const global = getGlobal();
  const user = global.currentUserId ? selectUser(global, global.currentUserId) : undefined;
  return Boolean(user && getChatAvatarHash(user, 'big'));
}

/** The caller's PERSONAL org id — media uploads are org-scoped. */
export async function fetchPersonalOrgId(): Promise<string | undefined> {
  const orgs = parseApi(schemas.OrgsSchema, await bileliApiJson('/me/orgs'), 'GET /me/orgs');
  return orgs.find((o) => o.kind === 'PERSONAL')?.id;
}

/**
 * Pull the Telegram profile photo's bytes and set it as the Bileli avatar.
 * Throws when there is no Telegram photo / no personal org — callers show the hand-off state instead.
 */
export async function syncTelegramAvatar(): Promise<BileliUser> {
  const global = getGlobal();
  const user = global.currentUserId ? selectUser(global, global.currentUserId) : undefined;
  const hash = user && getChatAvatarHash(user, 'big');
  if (!hash) throw new Error('NO_TELEGRAM_PHOTO');

  const blobUrl = mediaLoader.getFromMemory(hash) ?? await mediaLoader.fetch(hash, ApiMediaFormat.BlobUrl);
  const blob = await (await fetch(blobUrl)).blob();
  const file = new File([blob], 'telegram-avatar.jpg', { type: blob.type || 'image/jpeg' });

  const orgId = await fetchPersonalOrgId();
  if (!orgId) throw new Error('NO_PERSONAL_ORG');

  const mediaId = await uploadMedia(orgId, 'IMAGE', file);
  return setMyAvatar(mediaId);
}
